// Utility functions to test and verify Firestore data storage
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Test function to verify user data structure in Firestore
export const testUserDataStructure = async (userId) => {
  try {
    console.log('🔍 Testing Firestore data structure for user:', userId);
    
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log('✅ User data found in Firestore:');
      console.log('📋 Complete user document:', userData);
      
      // Verify required fields
      const requiredFields = ['uid', 'email', 'firstName', 'lastName', 'createdAt'];
      const missingFields = requiredFields.filter(field => !userData[field]);
      
      if (missingFields.length === 0) {
        console.log('✅ All required fields present');
      } else {
        console.log('⚠️ Missing required fields:', missingFields);
      }
      
      // Verify data structure
      console.log('📊 Data structure verification:');
      console.log('- Basic Info:', {
        name: userData.displayName,
        email: userData.email,
        phone: userData.phone || 'Not set'
      });
      
      console.log('- Address:', userData.address || 'Not set');
      console.log('- Preferences:', userData.preferences || 'Not set');
      console.log('- Profile Status:', userData.profile || 'Not set');
      
      console.log('- Timestamps:', {
        created: userData.createdAt,
        lastLogin: userData.lastLogin,
        updated: userData.updatedAt
      });
      
      return userData;
    } else {
      console.log('❌ No user document found in Firestore');
      return null;
    }
  } catch (error) {
    console.error('❌ Error testing Firestore data:', error);
    return null;
  }
};

// Get all users (for admin purposes - be careful with this in production)
export const getAllUsers = async () => {
  try {
    console.log('🔍 Fetching all users from Firestore...');
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const users = [];
    snapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log(`✅ Found ${users.length} users in Firestore`);
    console.log('👥 Users:', users.map(user => ({
      id: user.id,
      name: user.displayName,
      email: user.email,
      created: user.createdAt
    })));
    
    return users;
  } catch (error) {
    console.error('❌ Error fetching users:', error);
    return [];
  }
};

// Verify user data integrity
export const verifyUserDataIntegrity = async (userId) => {
  try {
    const userData = await testUserDataStructure(userId);
    
    if (!userData) {
      return false;
    }
    
    console.log('🔍 Verifying data integrity...');
    
    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(userData.email);
    console.log('📧 Email format valid:', isValidEmail);
    
    // Check required fields
    const hasRequiredFields = userData.uid && userData.email && userData.firstName && userData.lastName;
    console.log('📋 Required fields present:', hasRequiredFields);
    
    // Check timestamps
    const hasValidTimestamps = userData.createdAt && userData.lastLogin;
    console.log('⏰ Valid timestamps:', hasValidTimestamps);
    
    // Check nested objects
    const hasAddressStructure = userData.address && typeof userData.address === 'object';
    const hasPreferencesStructure = userData.preferences && typeof userData.preferences === 'object';
    const hasProfileStructure = userData.profile && typeof userData.profile === 'object';
    
    console.log('🏠 Address structure:', hasAddressStructure);
    console.log('⚙️ Preferences structure:', hasPreferencesStructure);
    console.log('👤 Profile structure:', hasProfileStructure);
    
    const isDataValid = isValidEmail && hasRequiredFields && hasValidTimestamps;
    console.log('✅ Overall data integrity:', isDataValid ? 'VALID' : 'INVALID');
    
    return isDataValid;
  } catch (error) {
    console.error('❌ Error verifying data integrity:', error);
    return false;
  }
};

// Expected Firestore document structure (for reference)
export const expectedUserDocumentStructure = {
  uid: "string - Firebase Auth user ID",
  email: "string - User email (lowercase)",
  firstName: "string - User first name",
  lastName: "string - User last name", 
  displayName: "string - Full name",
  phone: "string - Phone number (optional)",
  dateOfBirth: "string - Date in YYYY-MM-DD format (optional)",
  address: {
    street: "string - Street address",
    city: "string - City",
    state: "string - State/Province",
    zipCode: "string - ZIP/Postal code",
    country: "string - Country"
  },
  preferences: {
    emailNotifications: "boolean - Email notifications enabled",
    smsNotifications: "boolean - SMS notifications enabled", 
    marketingCommunications: "boolean - Marketing emails enabled"
  },
  profile: {
    isComplete: "boolean - Profile completion status",
    lastUpdated: "string - ISO timestamp of last profile update"
  },
  createdAt: "string - ISO timestamp of account creation",
  lastLogin: "string - ISO timestamp of last login",
  updatedAt: "string - ISO timestamp of last document update"
};

// Function to log expected structure
export const logExpectedStructure = () => {
  console.log('📋 Expected Firestore document structure:');
  console.log(JSON.stringify(expectedUserDocumentStructure, null, 2));
};
